import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Form, Formik } from "formik";

import { Container, Title } from "../../components/listing/AMSListing";
import StyledText from "../../components/StyledComponents/StyledText";
import { CorporateTheme, Theme } from "../../utils/themes";
import { Button } from "../../components/form/Button";
import CloseIcon from "../../assets/icons/CloseIcon";
import ArrowIcon from "../../assets/icons/ArrowIcon";
import SingleDocumentIcon from "../../assets/icons/SingleDocumentIcon";
import FormikCheckBox from "../../components/Formik/FormikCheckBox";
import {
  contactRolesApi,
  useGetRolesByIdQuery,
  useRevokePermissionsMutation,
} from "../../api/contactRoles";
import CustomRolesSidebarList from "./CustomRolesSidebarList";
import { useToast } from "../../components/toast/ToastContext";
import PermissionSetSkeletol from "./PermissionSetSkeletol";
import { formatText } from "../../utils/helper";
import { useTheme } from "../../middleware/ThemeContext";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { skipToken } from "@reduxjs/toolkit/query";
import AssignedContactsListing from "./AssignedContactsListing";
import { SkeletonCell } from "../../components/SkeletonCell";

interface PermissionRole {
  id: number;
  permissionGroup: string;
  name: string;
  description: string | null;
  accessKey: string;
}

type RolePermission = {
  id: number;
  roleId: number;
  permissionId: number;
  isDeleted: boolean;
  permission: PermissionRole;
};

export type ContactRoles = {
  contactOrgId: number;
  id: number;
  isDeleted: boolean;
  roleId: number;
  contactOrganization: {
    accountId: number;
    createdAt: string;
    id: number;
    organizationId: number;
    updatedAt: string;
    contact: {
      companyId: number;
      email: string;
      firstName: string;
      lastName: string;
      id: number;
      signedUrl: string;
    };
    organization: {
      id: number;
      name: string;
      signedUrl: string;
      industry: string | null;
    };
  };
};

const EditRolePermissions = () => {
  const { theme } = useTheme();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const persistenceTabKey = "roles-permissions-activeTab";

  const parseTabParam = (value?: string | null) => {
    return value === "assignedContacts" ? 1 : 0;
  };

  const storedTab =
    typeof window !== "undefined"
      ? window.localStorage.getItem(persistenceTabKey)
      : null;
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam
    ? parseTabParam(tabParam)
    : storedTab === "assignedContacts"
      ? 1
      : 0;
  const initialRoleId = id ? Number(id) : undefined;

  const [allPermissions, setAllPermissions] = useState<Record<
    string,
    PermissionRole[]
  > | null>(null);
  const [roleID, setRoleID] = useState<number | undefined>(
    Number.isFinite(initialRoleId as number) ? initialRoleId : undefined,
  );
  const [roleName, setRoleName] = useState<string>("");
  const [contactRoles, setContactRoles] = useState<ContactRoles[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(initialTab);
  const [revokePermissions] = useRevokePermissionsMutation();
  const { data, refetch, isLoading, isFetching } = useGetRolesByIdQuery(
    roleID ? roleID : skipToken,
  );
  const fetchAllPermissions = async () => {
    try {
      const permissions = await contactRolesApi.getAllPermissions();
      const grouped = permissions.roles.reduce(
        (acc: Record<string, PermissionRole[]>, role: PermissionRole) => {
          const group = role.permissionGroup;
          if (!acc[group]) acc[group] = [];
          acc[group].push(role);
          return acc;
        },
        {},
      );
      setAllPermissions(grouped);
    } catch (error) {
      console.error("Failed to fetch permissions", error);
    }
  };

  useEffect(() => {
    fetchAllPermissions();
  }, []);

  useEffect(() => {
    if (roleID) {
      const fetchFreshData = async () => {
        await refetch();
      };
      fetchFreshData();
    }
  }, [roleID]);

  useEffect(() => {
    if (data) {
      setContactRoles(data?.contactRoles);
      if (data.name && data.name !== roleName) {
        setRoleName(data.name);
      }
    } else {
      setContactRoles([]);
    }
  }, [data, roleName]);

  useEffect(() => {
    if (id) {
      const parsedId = Number(id);
      if (Number.isFinite(parsedId) && parsedId !== roleID) {
        setRoleID(parsedId);
      }
    }
  }, [id]);

  useEffect(() => {
    if (!id && roleID) {
      const params = new URLSearchParams(searchParams);
      if (!params.has("tab")) {
        params.set(
          "tab",
          activeTab === 1 ? "assignedContacts" : "permissionSet",
        );
      }
      navigate(
        `/home/roles-permissions/${roleID}${params.toString() ? `?${params.toString()}` : ""}`,
        { replace: true },
      );
    }
  }, [id, roleID, activeTab, navigate, searchParams]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const nextTab = activeTab === 1 ? "assignedContacts" : "permissionSet";
      window.localStorage.setItem(persistenceTabKey, nextTab);
      const params = new URLSearchParams(searchParams);
      params.set("tab", nextTab);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const handleSelectRole = (selectedId: number) => {
    setRoleID(selectedId);
    const params = new URLSearchParams(searchParams);
    if (!params.has("tab")) {
      params.set("tab", activeTab === 1 ? "assignedContacts" : "permissionSet");
    }
    navigate(
      `/home/roles-permissions/${selectedId}${params.toString() ? `?${params.toString()}` : ""}`,
      { replace: true },
    );
  };

  const handleTabChange = (tab: number) => {
    setActiveTab(tab);
  };

  // const selectedPermissionAccessKeys = Array.isArray(data?.rolePermissions)
  //   ? data?.rolePermissions
  //     .filter((rp: RolePermission) => !rp.isDeleted)
  //     .map((rp: RolePermission) => rp.permission.accessKey)
  //   : [];
  const selectedPermissionAccessKeys = Array.isArray(data?.rolePermissions)
    ? data.rolePermissions
        .filter((rp: RolePermission) => !rp.isDeleted && rp.permission)
        .map((rp: RolePermission) => rp.permission.accessKey)
    : [];

  const initialValues = allPermissions
    ? Object.values(allPermissions)
        .flat()
        .reduce((acc: Record<string, boolean>, permission) => {
          acc[permission.accessKey] = selectedPermissionAccessKeys.includes(
            permission.accessKey,
          );
          return acc;
        }, {})
    : {};

  const handleSubmit = async (values: Record<string, boolean>) => {
    if (!roleID || !allPermissions) return;
    setSaveLoading(true);
    const flatPermissions = Object.values(allPermissions).flat();
    const selectedPermissions = flatPermissions
      .filter((perm) => values[perm.accessKey])
      .map((perm) => perm.name);
    const selectedPermissionIds = flatPermissions
      .filter((perm) => values[perm.accessKey])
      .map((perm) => perm.id);

    const formattedPermissions = selectedPermissions.join(", ");

    try {
      await contactRolesApi.updateRole(roleID, {
        permissions: selectedPermissionIds,
      });
      showToast(
        "Success",
        `Permissions updated successfully: ${formattedPermissions}`,
        "success",
      );
      refetch();
    } catch (error) {
      console.error("Failed to update role", error);
      showToast("Error", "Failed to update role permissions", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const setDefault = async (id: number, resetForm: () => void) => {
    if (!roleID) return;

    try {
      setResetLoading(true);

      await revokePermissions(id);

      showToast(
        "Permissions Removed",
        "The permissions of the selected role have been removed.",
        "success",
      );
      refetch();
      resetForm();
      // await fetchData(id);
      // handleFetchRequested(lastParamsRef.current);
    } catch (error) {
      showToast(
        "Failed",
        "Failed to remove permissions for the selected role. Please try again.",
        "error",
      );
      console.error("setDefault error:", error);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <Container theme={theme} className="rounded-0">
        <div className="d-flex justify-content-between mt-0">
          <div>
            <div className="d-flex align-items-center gap-2">
              <ArrowIcon
                className="cursor-pointer"
                onClick={() => navigate("/home/contact-roles")}
                color={theme.CorporateTheme.colors.textSecondary}
              />
              <Title theme={theme}>Roles & Their Permissions</Title>
            </div>
            <StyledText
              fontSize="14px"
              fontWeight={300}
              textColor={theme.CorporateTheme.colors.textSecondary}
            >
              All system and custom roles with their permission will show here
            </StyledText>
          </div>
        </div>
      </Container>

      <div
        className="container-fluid"
        style={{
          backgroundColor: theme.CorporateTheme.colors.bannerBg,
          height: "calc(100vh - 198px)",
        }}
      >
        <div className="row pt-3 px-2">
          <div className="col-4">
            <StyledText
              fontSize="16px"
              style={{ marginBottom: "20px", marginTop: "12px" }}
            >
              All Roles
            </StyledText>
            <CustomRolesSidebarList
              selectedID={roleID}
              onClick={handleSelectRole}
              setRoleName={setRoleName}
              lockSelection={roleID !== undefined}
            />
          </div>
          <div className="col-8">
            <Formik
              initialValues={initialValues}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ handleSubmit, resetForm }) => (
                <Form onSubmit={handleSubmit}>
                  <div
                    className="d-flex align-items-center justify-content-between"
                    style={{ marginBottom: "10px" }}
                  >
                    <StyledText fontSize="16px">
                      {roleName !== "" ? (
                        roleName
                      ) : (
                        <SkeletonCell
                          style={{ width: "90px", height: "10px" }}
                        />
                      )}
                    </StyledText>
                    <div className="col-5 d-flex gap-2 justify-content-end">
                      {data?.roleType === "Custom" &&
                      data?.rolePermissions.length > 0 ? (
                        <Button
                          theme={theme}
                          type="button"
                          $variant="cancel"
                          disabled={resetLoading}
                          onClick={() => setDefault(roleID!, resetForm)}
                        >
                          {resetLoading ? (
                            <LoadingSpinner
                              color={theme.CorporateTheme.colors.textSecondary}
                              size="16px"
                            />
                          ) : (
                            <CloseIcon
                              color={theme.CorporateTheme.colors.textSecondary}
                            />
                          )}{" "}
                          Reset to default
                        </Button>
                      ) : null}
                      <Button
                        theme={theme}
                        type="submit"
                        $variant="add"
                        className={`${data?.roleType !== "Custom" ? "cursor-not-allowed" : ""}`}
                        disabled={
                          saveLoading || data?.roleType === "Custom"
                            ? false
                            : true
                        }
                      >
                        {saveLoading ? (
                          <LoadingSpinner
                            color={theme.CorporateTheme.colors.background}
                            size="16px"
                          />
                        ) : (
                          <SingleDocumentIcon
                            color={theme.CorporateTheme.colors.background}
                          />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                  <WhiteContainer
                    theme={theme}
                    style={{ height: "calc(100vh - 278px)", overflowY: "auto" }}
                  >
                    <div className="d-flex gap-4 border-bottom mb-3">
                      <TabsCon
                        theme={theme}
                        className={`pb-3 ${activeTab === 0 ? "active" : ""}`}
                        onClick={() => handleTabChange(0)}
                      >
                        <StyledText
                          textColor={
                            activeTab === 0
                              ? theme.CorporateTheme.colors.primary
                              : theme.CorporateTheme.colors.headerColor
                          }
                        >
                          Permission Set
                        </StyledText>
                      </TabsCon>
                      <TabsCon
                        theme={theme}
                        className={`pb-3 ${activeTab === 1 ? "active" : ""}`}
                        onClick={() => handleTabChange(1)}
                      >
                        <StyledText
                          textColor={
                            activeTab === 1
                              ? theme.CorporateTheme.colors.primary
                              : theme.CorporateTheme.colors.headerColor
                          }
                        >
                          Assigned Contacts
                        </StyledText>
                      </TabsCon>
                    </div>
                    {activeTab === 0 ? (
                      !isLoading && !isFetching ? (
                        <>
                          <div className="d-flex gap-3 align-items-center justify-content-between">
                            <StyledText
                              fontSize="14px"
                              fontWeight={500}
                              className="fst-italic"
                            >
                              All selected and available permissions against the
                              selected role will display here
                            </StyledText>
                            {/* <div className="col-5 d-flex gap-2 justify-content-end">
                          {data?.roleType === 'Custom' && data?.rolePermissions.length > 0 ?
                            <Button theme={theme} type="button" $variant="cancel" disabled={resetLoading} onClick={() => setDefault(roleID!, resetForm)}>
                              {resetLoading ? <LoadingSpinner color={theme.CorporateTheme.colors.textSecondary} size='16px' /> : <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />} Reset to default
                            </Button>
                            : null
                          }
                          <Button
                            theme={theme}
                            type="submit"
                            $variant="add"
                            className={`${data?.roleType !== 'Custom' ? 'cursor-not-allowed' : ''}`}
                            disabled={saveLoading || data?.roleType === 'Custom' ? false : true}
                          >
                            {saveLoading ? <LoadingSpinner color={theme.CorporateTheme.colors.background} size='16px' /> : <SingleDocumentIcon color={theme.CorporateTheme.colors.background} />}
                            Save
                          </Button>
                        </div> */}
                          </div>

                          <PermissionsLists
                            className="all-permissions"
                            theme={theme}
                          >
                            {allPermissions &&
                              Object.entries(allPermissions).map(
                                ([group, permissions]) => (
                                  <div
                                    className="the-permission mb-2"
                                    key={group}
                                  >
                                    <StyledText
                                      className="my-4"
                                      fontSize="16px"
                                      fontWeight={600}
                                      textColor={
                                        theme.CorporateTheme.colors.whiteBlack
                                      }
                                    >
                                      {group === "CONTACT"
                                        ? "Contacts (Inside organization/Chapters/Committee"
                                        : group === "FORMS"
                                          ? "Forms & Submissions"
                                          : group === "EVENTS"
                                            ? "Events & Programs"
                                            : group === "CHAPERS"
                                              ? "Chapters / Committees"
                                              : formatText(group) +
                                                " Permissions"}
                                    </StyledText>
                                    <div className="row ">
                                      {permissions?.map(
                                        (permission: PermissionRole) => (
                                          <PermissionSet
                                            className="col-12 col-xxl-6 mb-3"
                                            key={permission.id}
                                          >
                                            <FormikCheckBox
                                              name={permission.accessKey}
                                              label={permission.name}
                                              borderColor={
                                                theme.CorporateTheme.colors
                                                  .headerColor
                                              }
                                              bgColor={
                                                theme.CorporateTheme.colors
                                                  .headerColor
                                              }
                                              checkColor={
                                                theme.colors.contentBackground
                                              }
                                              color={
                                                theme.CorporateTheme.colors
                                                  .textSecondary
                                              }
                                              disabled={
                                                data?.roleType !== "Custom"
                                              }
                                              className={`${data?.roleType !== "Custom" ? "cursor-not-allowed" : ""}`}
                                              size={16}
                                            />
                                          </PermissionSet>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                          </PermissionsLists>
                        </>
                      ) : (
                        <PermissionSetSkeletol />
                      )
                    ) : // }
                    activeTab === 1 ? (
                      <AssignedContactsListing
                        contactRoles={contactRoles}
                        isLoading={isLoading || isFetching}
                      />
                    ) : null}
                  </WhiteContainer>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export const WhiteContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.colors.contentBackground,
  borderRadius: "5px",
  padding: "12px 20px",
  // marginTop: '20px',
}));

const PermissionsLists = styled("div")(({ theme }) => ({
  ".the-permission:not(:last-child)": {
    paddingBottom: "15px",
    borderBottom: `1px solid ${theme.CorporateTheme.colors.border}`,
  },
}));

const TabsCon = styled("div")(({ theme }) => ({
  cursor: "pointer",
  color: `${theme.CorporateTheme.colors.headerColor}`,
  "&.active": {
    "border-bottom": `2px solid ${theme.CorporateTheme.colors.primary}`,
    color: `${theme.CorporateTheme.colors.primary}`,
  },
}));

const PermissionSet = styled.div`
  @media screen and (min-width: 1850px){
    width: 33.333333%;
    flex: 0 0 33.333333%;
  }
`;

export default EditRolePermissions;
